import React from "react";
import { proposalEvidence } from "../helpers/teamProposal";
export default function MetricExplanation({
  data,
  teamId,
  model,
  question,
  field,
  recent,
  en,
  compact,
  steps,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", { maximumFractionDigits: 3 })
        : "—";
  const ev = proposalEvidence(data, teamId, model, question, recent, field);
  const labels = {
    should: t("Should Goals · Saison", "Should Goals · season"),
    real: t("Reale Ergebnisse", "Actual results"),
    market: t("Marktrückrechnung", "Market reconstruction"),
    recent: t(`Leistung · letzte ${recent}`, `Performance · last ${recent}`),
    external: t("Externe xGoals", "External xGoals"),
    other: t("Anderes Modell · gespeichert", "Other model · saved"),
    previous: t(
      "Vorsaison · anderer Zeitraum",
      "Previous season · different period"
    ),
  };
  const text = field
    ? t(
        "Offensive bedeutet eigene Tore, Defensive Gegentore pro Spiel. Gesamtsettings mitteln Heim und Auswärts gleich. Historische Werte berücksichtigen die tatsächlich gespielten Gegner.",
        "Attack means goals scored, defence goals conceded per match. Overall settings weight home and away equally. Historical values reflect the opponents actually played."
      )
    : question === "strength"
    ? t(
        "Spielstärke ist Für / Gegen. Über 1 bedeutet mehr eigene Tore als Gegentore, unter 1 weniger. Eine reine Quotientenänderung erhält das Torniveau.",
        "Strength is goals for / against. Above 1 means more goals scored than conceded; below 1 fewer. A ratio-only edit preserves goal level."
      )
    : question === "goals"
    ? t(
        "Torniveau ist Für + Gegen pro Spiel. Eine reine Torniveauänderung erhält den Quotienten.",
        "Goal level is goals for + against per match. Editing only goal level preserves the ratio."
      )
    : question === "home"
    ? t(
        "Heimvorteil ist der Quotient aus offensivem und defensivem Heimfaktor. Beide Faktoren entstehen aus den Heimwerten relativ zu ihren Gesamtwerten.",
        "Home advantage is the ratio of attacking and defensive home factors. Each factor compares the home value with its overall value."
      )
    : t(
        "Je niedriger der Unentschieden-Faktor, desto mehr Remisen bei sonst gleichen Parametern. Das Ergebnismodell verwendet Teamfaktoren; die vorhandene Statistikmodell-Berechnung verwendet den Ligafaktor.",
        "The lower the draw factor, the more draws with other parameters unchanged. The results model uses team factors; the existing statistics-model calculation uses the league factor."
      );
  if (question === "draws")
    return (
      <section className="metric-explanation">
        <h2>{t("Unentschieden-Faktor", "Draw factor")}</h2>
        <p>{text}</p>
        <p>
          {t("Gespeicherter Teamfaktor", "Saved team factor")}:{" "}
          <b>{fmt(data.result_teams?.[teamId]?.draw_factor)}</b> ·{" "}
          {t("Ligafaktor", "League factor")}:{" "}
          {fmt(data.result_parameters?.drawfactor)}
        </p>
        {steps.map((s) => (
          <details key={s.key}>
            <summary>
              {s.title}: {fmt(s.value)} {s.unit}
            </summary>
            <p>{s.compact}</p>
            <p>{s.coverage}</p>
          </details>
        ))}
        <p>
          {t(
            "Der vorhandene Neubewertungsvorschlag dämpft die Differenz aus realen und erwarteten Remisen mit n+140. Seine Wirkung kann gegen einen Ranking-Nachbarn heim/auswärts geschätzt werden.",
            "The existing reassessment dampens actual minus expected draw counts using n+140. Its effect can be estimated against a ranking neighbour, home and away."
          )}
        </p>
      </section>
    );
  return (
    <section className="metric-explanation">
      <h2>
        {t("Gespeicherter Wert", "Saved value")}: {fmt(ev.saved)}
      </h2>
      <p>{text}</p>
      <p>
        {field || question === "goals"
          ? t("Einheit: Tore pro Spiel.", "Unit: goals per match.")
          : t(
              "Einheit: dimensionsloser Quotient.",
              "Unit: dimensionless ratio."
            )}
      </p>
      {!compact && (
        <p>
          {t(
            "Erwartungen stammen aus den jeweiligen historischen Begegnungen. Verglichen werden je Zeile dieselben Spiele. Für die Anpassung zählt Leistung minus Erwartung; eine gespeicherte Einstellung ist keine historische Erwartung.",
            "Expectations come from the historical fixtures. Each row compares the same matches. Adjustment uses performance minus expectation; a saved setting is not a historical expectation."
          )}
        </p>
      )}
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Vergleich", "Comparison")}</th>
              <th>{t("Wert", "Value")}</th>
              <th>{t("Erwartet · gleiche Spiele", "Expected · same games")}</th>
              <th>{t("Spiele", "Matches")}</th>
            </tr>
          </thead>
          <tbody>
            {ev.sources.map((s) => (
              <tr key={s.key}>
                <th>{labels[s.key]}</th>
                <td>
                  {fmt(
                    s.observed ??
                      (["other", "previous"].includes(s.key) ? s.full : null)
                  )}
                </td>
                <td>{fmt(s.expected)}</td>
                <td>
                  {s.count ?? "—"}
                  {s.total != null ? `/${s.total}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!compact && (
        <details>
          <summary>
            {t("Herkunft und Herleitung", "Provenance and derivation")}
          </summary>
          {steps.map((s) => (
            <div key={s.key}>
              <b>
                {s.title}: {fmt(s.value)}
              </b>
              <p>
                {s.compact} {s.coverage}
              </p>
              {s.details?.map((d, i) => (
                <p key={i}>{d}</p>
              ))}
            </div>
          ))}
          <p>
            {t(
              "Should Goals sind unsere Leistungsmessung; externe xGoals eine unabhängige Methode. Fehlende externe Daten werden nicht ersetzt. Eine Abweichung allein beweist keinen Fehler.",
              "Should Goals are our performance measure; external xGoals use an independent method. Missing external values are not replaced. A difference alone does not prove an error."
            )}
          </p>
        </details>
      )}
    </section>
  );
}
